import React, { useState } from 'react';
import './OneWayFlightCard.css';

// ═══════════════════════════════════════════════════════════════════════════════
// AIRGATI ONE-WAY FLIGHT CARD - v2.0
// Fixed: Time display, Total price, Better data extraction
// ═══════════════════════════════════════════════════════════════════════════════

const OneWayFlightCard = ({ flight, lastSearchParams, onBook, isBestDeal = false }) => {
  const [showAllOptions, setShowAllOptions] = useState(false);
  
  // Debug: Log flight data to console (remove in production)
  // console.log('OneWayFlightCard received:', { flight, lastSearchParams });
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // HELPER FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════════════════
  
  // Format ISO time to readable format (6:55 PM)
  const formatTime = (timeValue) => {
    if (!timeValue) return null;
    
    // If already formatted (e.g., "6:55 PM"), return as is
    if (typeof timeValue === 'string' && (timeValue.includes('AM') || timeValue.includes('PM'))) {
      return timeValue;
    }
    
    try {
      // Handle ISO string like "2025-02-15T18:55:00"
      const date = new Date(timeValue);
      if (isNaN(date.getTime())) return null;
      
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit', 
        hour12: true 
      });
    } catch {
      return null;
    }
  };
  
  // Format ISO duration (PT18H30M or PT24H5M) to readable format (18h 30m)
  const formatDuration = (isoDuration) => {
    if (!isoDuration) return null;
    
    // If already formatted, return as is
    if (typeof isoDuration === 'string' && (isoDuration.includes('h ') || isoDuration.includes('hr'))) {
      return isoDuration;
    }
    
    // Parse ISO 8601 duration: PT24H5M, PT18H30M, PT5H, etc.
    const match = isoDuration.match(/PT(\d+)H(?:(\d+)M)?/);
    if (match) {
      const hours = match[1];
      const mins = match[2] || '0';
      return `${hours}h ${mins}m`;
    }
    
    return isoDuration;
  };
  
  // Check if arrival is next day
  const isNextDay = (departure, arrival) => {
    if (!departure || !arrival) return false;
    try {
      const depDate = new Date(departure).toDateString();
      const arrDate = new Date(arrival).toDateString();
      return depDate !== arrDate;
    } catch {
      return false;
    }
  };
  
  // Format date for display (Sun, Feb 15)
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      // Handle both "2025-02-15" and "2025-02-15T18:55:00" formats
      const dateOnly = dateStr.split('T')[0];
      const date = new Date(dateOnly + 'T12:00:00'); // Use noon to avoid timezone issues
      if (isNaN(date.getTime())) return dateStr;
      
      return date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // DATA EXTRACTION - Handle multiple API response formats
  // ═══════════════════════════════════════════════════════════════════════════════
  
  // Get outbound data from nested structure
  const outbound = flight?.outbound || {};
  const segments = outbound.segments || [];
  const firstSegment = segments[0] || {};
  const lastSegment = segments[segments.length - 1] || segments[0] || {};
  
  // Get price - handle both object and number formats
  const getPrice = () => {
    if (typeof flight?.price === 'number') return flight.price;
    if (typeof flight?.price === 'object') {
      return flight.price.perPerson || flight.price.total || 0;
    }
    return 0;
  };
  
  // Get total price
  const getTotalPrice = () => {
    if (typeof flight?.price === 'object' && flight.price.total) {
      return flight.price.total;
    }
    const perPerson = getPrice();
    const passengers = lastSearchParams?.passengers || lastSearchParams?.adults || 1;
    return perPerson * passengers;
  };
  
  // Get number of passengers
  const getPassengers = () => {
    return lastSearchParams?.passengers || lastSearchParams?.adults || 1;
  };
  
  // Get airline name - handle both object and string formats
  const getAirlineName = () => {
    if (typeof flight?.airline === 'string') return flight.airline;
    if (typeof flight?.airline === 'object') return flight.airline.name || 'Airline';
    return 'Airline';
  };
  
  // Get airline code
  const getAirlineCode = () => {
    if (flight?.airlineCode) return flight.airlineCode;
    if (typeof flight?.airline === 'object') return flight.airline.code || '';
    return '';
  };
  
  // Get departure time - check multiple sources
  const getDepartureTime = () => {
    // Priority 1: outbound.departure (ISO string from Amadeus)
    if (outbound.departure) return formatTime(outbound.departure);
    // Priority 2: first segment departure
    if (firstSegment.departure) return formatTime(firstSegment.departure);
    // Priority 3: direct departureTime field
    if (flight?.departureTime) return formatTime(flight.departureTime);
    return null;
  };
  
  // Get arrival time - check multiple sources
  const getArrivalTime = () => {
    // Priority 1: outbound.arrival (ISO string from Amadeus)
    if (outbound.arrival) return formatTime(outbound.arrival);
    // Priority 2: last segment arrival
    if (lastSegment.arrival) return formatTime(lastSegment.arrival);
    // Priority 3: direct arrivalTime field
    if (flight?.arrivalTime) return formatTime(flight.arrivalTime);
    return null;
  };
  
  // Get duration
  const getDuration = () => {
    if (outbound.duration) return formatDuration(outbound.duration);
    if (flight?.duration) return formatDuration(flight.duration);
    return null;
  };
  
  // Get origin airport code
  const getOrigin = () => {
    if (firstSegment.from) return firstSegment.from;
    if (flight?.origin) return flight.origin;
    if (lastSearchParams?.origin) return lastSearchParams.origin;
    if (lastSearchParams?.from) return lastSearchParams.from;
    return '';
  };
  
  // Get destination airport code
  const getDestination = () => {
    if (lastSegment.to) return lastSegment.to;
    if (flight?.destination) return flight.destination;
    if (lastSearchParams?.destination) return lastSearchParams.destination;
    if (lastSearchParams?.to) return lastSearchParams.to;
    return '';
  };
  
  // Get departure date
  const getDepartureDate = () => {
    if (outbound.departure) return outbound.departure.split('T')[0];
    if (firstSegment.departure) return firstSegment.departure.split('T')[0];
    if (flight?.departureDate) return flight.departureDate;
    if (lastSearchParams?.departDate) return lastSearchParams.departDate;
    if (lastSearchParams?.departureDate) return lastSearchParams.departureDate;
    return '';
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // EXTRACTED VALUES
  // ═══════════════════════════════════════════════════════════════════════════════
  
  const price = getPrice();
  const totalPrice = getTotalPrice();
  const passengers = getPassengers();
  const airlineName = getAirlineName();
  const airlineCode = getAirlineCode();
  const source = flight?.source || 'unknown';
  
  const departureTime = getDepartureTime();
  const arrivalTime = getArrivalTime();
  const duration = getDuration();
  const origin = getOrigin();
  const destination = getDestination();
  const departureDate = getDepartureDate();
  
  // Stops info
  const stops = outbound.stops ?? flight?.stops ?? 0;
  const stopCities = outbound.stopCities || flight?.stopCities || [];
  const stopsText = stops === 0 
    ? 'Nonstop' 
    : `${stops} stop${stops > 1 ? 's' : ''}`;
  
  // Flight numbers
  const flightNumbers = outbound.flightNumbers || 
                        (flight?.flightNumber ? [flight.flightNumber] : []);
  
  // Next day arrival?
  const nextDay = isNextDay(outbound.departure, outbound.arrival);

  // ═══════════════════════════════════════════════════════════════════════════════
  // BOOKING OPTIONS
  // ═══════════════════════════════════════════════════════════════════════════════
  
  const getBookingOptions = () => {
    // If flight already has formatted bookingOptions, use them
    if (flight?.bookingOptions && flight.bookingOptions.length > 0) {
      return flight.bookingOptions.map((opt, idx) => ({
        id: opt.id || `opt-${idx}`,
        name: opt.name,
        logo: opt.logo,
        isImage: true,
        price: opt.price || price,
        priceLabel: opt.priceLabel || `$${Math.round(opt.price || price)}`,
        isBest: opt.isBest || idx === 0,
        url: opt.url,
        badge: opt.isBest || idx === 0 ? 'Best Price' : null,
        isDeepLink: opt.isDeepLink || opt.affiliate
      }));
    }
    
    // Build from bookingLinks array (API format)
    if (flight?.bookingLinks && flight.bookingLinks.length > 0) {
      return flight.bookingLinks.map((link, idx) => ({
        id: link.name?.toLowerCase().replace(/\s/g, '-') || `link-${idx}`,
        name: link.name,
        logo: link.logo,
        isImage: true,
        price: price,
        priceLabel: `$${Math.round(price)}`,
        isBest: idx === 0,
        url: link.url,
        badge: idx === 0 ? 'Best Price' : null,
        isDeepLink: link.affiliate
      }));
    }
    
    // Default fallback
    return [{
      id: 'search',
      name: 'Search Flights',
      logo: 'https://pics.avs.io/70/70/@s.png',
      isImage: true,
      price: price,
      priceLabel: price ? `$${Math.round(price)}` : 'Search',
      isBest: true,
      url: flight?.deepLink || '#',
      badge: null,
      isDeepLink: false
    }];
  };
  
  const bookingOptions = getBookingOptions();
  const displayedOptions = showAllOptions ? bookingOptions : bookingOptions.slice(0, 4);
  
  // Handle booking click
  const handleBookClick = (option, e) => {
    e.stopPropagation();
    if (option.url && option.url !== '#') {
      window.open(option.url, '_blank', 'noopener,noreferrer');
    }
    if (onBook) {
      onBook({ flight, option });
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // STYLES
  // ═══════════════════════════════════════════════════════════════════════════════
  
  const styles = {
    card: {
      background: '#ffffff',
      borderRadius: '16px',
      overflow: 'hidden',
      marginBottom: '16px',
      border: isBestDeal ? '2px solid #fbbf24' : '1px solid #e5e7eb',
      boxShadow: isBestDeal 
        ? '0 4px 16px rgba(251, 191, 36, 0.15)' 
        : '0 2px 8px rgba(0,0,0,0.04)',
      position: 'relative',
      transition: 'all 0.2s ease'
    },
    bestDealBadge: {
      position: 'absolute',
      top: '0',
      left: '20px',
      background: 'linear-gradient(145deg, #fbbf24, #f59e0b)',
      color: '#18181b',
      padding: '4px 12px',
      borderRadius: '0 0 8px 8px',
      fontSize: '10px',
      fontWeight: '700',
      textTransform: 'uppercase',
      zIndex: 1
    },
    flightMain: {
      padding: '20px',
      paddingTop: isBestDeal ? '28px' : '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '20px',
      flexWrap: 'wrap'
    },
    airlineSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      minWidth: '160px'
    },
    airlineLogo: {
      width: '48px',
      height: '48px',
      background: 'linear-gradient(145deg, #f4f4f5, #e4e4e7)',
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      flexShrink: 0
    },
    airlineLogoImg: {
      width: '100%',
      height: '100%',
      objectFit: 'contain'
    },
    airlineInfo: {
      display: 'flex',
      flexDirection: 'column'
    },
    airlineName: {
      fontSize: '14px',
      fontWeight: '700',
      color: '#18181b',
      textTransform: 'uppercase'
    },
    flightNumber: {
      fontSize: '11px',
      color: '#71717a',
      marginTop: '2px'
    },
    flightRoute: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      minWidth: '320px'
    },
    timeBlock: {
      textAlign: 'center',
      minWidth: '80px'
    },
    time: {
      fontSize: '20px',
      fontWeight: '700',
      color: '#18181b',
      lineHeight: 1.2
    },
    noTime: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#a1a1aa',
      lineHeight: 1.2
    },
    nextDay: {
      fontSize: '10px',
      color: '#f59e0b',
      fontWeight: '700',
      verticalAlign: 'super',
      marginLeft: '2px'
    },
    airport: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#71717a',
      marginTop: '4px'
    },
    date: {
      fontSize: '11px',
      color: '#a1a1aa',
      marginTop: '2px'
    },
    flightPath: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      minWidth: '140px',
      padding: '0 8px'
    },
    duration: {
      fontSize: '12px',
      fontWeight: '600',
      color: '#52525b',
      marginBottom: '8px'
    },
    pathContainer: {
      width: '100%',
      position: 'relative',
      height: '20px',
      display: 'flex',
      alignItems: 'center'
    },
    pathLine: {
      width: '100%',
      height: '2px',
      background: 'linear-gradient(90deg, #fbbf24 0%, #e5e7eb 20%, #e5e7eb 80%, #fbbf24 100%)',
      position: 'relative'
    },
    pathDotLeft: {
      position: 'absolute',
      left: '0',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '8px',
      height: '8px',
      background: '#fbbf24',
      borderRadius: '50%'
    },
    pathDotRight: {
      position: 'absolute',
      right: '0',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '8px',
      height: '8px',
      background: '#fbbf24',
      borderRadius: '50%'
    },
    planeIcon: {
      position: 'absolute',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      background: 'white',
      padding: '0 6px',
      fontSize: '14px',
      zIndex: 1
    },
    stopsInfo: {
      marginTop: '8px',
      fontSize: '11px',
      fontWeight: '600'
    },
    stopsNonstop: {
      color: '#059669'
    },
    stopsHas: {
      color: '#f59e0b'
    },
    stopCities: {
      color: '#71717a',
      fontWeight: '400',
      marginLeft: '4px'
    },
    priceSection: {
      textAlign: 'right',
      minWidth: '110px'
    },
    priceLabel: {
      fontSize: '10px',
      color: '#a1a1aa',
      textTransform: 'uppercase',
      fontWeight: '600'
    },
    priceAmount: {
      fontSize: '28px',
      fontWeight: '800',
      color: '#059669',
      lineHeight: 1
    },
    pricePer: {
      fontSize: '11px',
      color: '#71717a',
      marginTop: '2px'
    },
    priceTotal: {
      fontSize: '11px',
      color: '#a1a1aa',
      marginTop: '2px'
    },
    bookingSection: {
      padding: '16px 20px',
      background: '#fafafa',
      borderTop: '1px solid #e5e7eb'
    },
    bookingHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '12px'
    },
    bookingTitle: {
      fontSize: '12px',
      fontWeight: '600',
      color: '#71717a'
    },
    showMoreBtn: {
      background: 'none',
      border: 'none',
      color: '#f59e0b',
      fontSize: '12px',
      fontWeight: '600',
      cursor: 'pointer',
      padding: 0
    },
    bookingRows: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    bookingRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px 16px',
      background: '#ffffff',
      border: '1px solid #e4e4e7',
      borderRadius: '10px',
      cursor: 'pointer',
      transition: 'all 0.15s ease'
    },
    bookingRowBest: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px 16px',
      background: 'linear-gradient(145deg, #fffbeb, #fef3c7)',
      border: '1px solid #fbbf24',
      borderRadius: '10px',
      cursor: 'pointer',
      transition: 'all 0.15s ease'
    },
    rowLogo: {
      width: '28px',
      height: '28px',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    rowLogoImg: {
      width: '24px',
      height: '24px',
      objectFit: 'contain',
      borderRadius: '4px'
    },
    rowName: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#18181b',
      flex: 1
    },
    rowBadge: {
      fontSize: '9px',
      fontWeight: '700',
      padding: '3px 8px',
      borderRadius: '4px',
      background: 'linear-gradient(145deg, #22c55e, #16a34a)',
      color: '#ffffff',
      textTransform: 'uppercase'
    },
    rowBadgeAffiliate: {
      fontSize: '9px',
      fontWeight: '700',
      padding: '3px 8px',
      borderRadius: '4px',
      background: '#dbeafe',
      color: '#2563eb',
      textTransform: 'uppercase'
    },
    rowPrice: {
      fontSize: '15px',
      fontWeight: '700',
      color: '#18181b'
    },
    rowPriceBest: {
      fontSize: '15px',
      fontWeight: '700',
      color: '#b45309'
    },
    rowArrow: {
      fontSize: '14px',
      color: '#a1a1aa'
    },
    dataSource: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      marginTop: '12px',
      fontSize: '11px',
      color: '#71717a'
    },
    sourceDot: {
      width: '6px',
      height: '6px',
      borderRadius: '50%',
      background: source === 'travelpayouts' ? '#22c55e' : source === 'amadeus' ? '#3b82f6' : '#a1a1aa'
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════════
  
  return (
    <div style={styles.card}>
      {/* Best Deal Badge */}
      {isBestDeal && (
        <div style={styles.bestDealBadge}>🏆 Best Deal</div>
      )}
      
      {/* Main Flight Info */}
      <div style={styles.flightMain}>
        {/* Airline */}
        <div style={styles.airlineSection}>
          <div style={styles.airlineLogo}>
            {airlineCode ? (
              <img 
                src={`https://pics.avs.io/70/70/${airlineCode}.png`}
                alt={airlineName}
                style={styles.airlineLogoImg}
                onError={(e) => { 
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '✈️';
                }}
              />
            ) : (
              <span style={{ fontSize: '24px' }}>✈️</span>
            )}
          </div>
          <div style={styles.airlineInfo}>
            <div style={styles.airlineName}>{airlineName}</div>
            {flightNumbers.length > 0 && (
              <div style={styles.flightNumber}>{flightNumbers.join(', ')}</div>
            )}
          </div>
        </div>
        
        {/* Route */}
        <div style={styles.flightRoute}>
          {/* Departure */}
          <div style={styles.timeBlock}>
            {departureTime ? (
              <div style={styles.time}>{departureTime}</div>
            ) : (
              <div style={styles.noTime}>--:--</div>
            )}
            <div style={styles.airport}>{origin}</div>
            <div style={styles.date}>{formatDate(departureDate)}</div>
          </div>
          
          {/* Flight Path */}
          <div style={styles.flightPath}>
            {duration && <div style={styles.duration}>{duration}</div>}
            <div style={styles.pathContainer}>
              <div style={styles.pathLine}>
                <span style={styles.pathDotLeft}></span>
                <span style={styles.planeIcon}>✈️</span>
                <span style={styles.pathDotRight}></span>
              </div>
            </div>
            <div style={{...styles.stopsInfo, ...(stops === 0 ? styles.stopsNonstop : styles.stopsHas)}}>
              {stopsText}
              {stopCities.length > 0 && (
                <span style={styles.stopCities}>({stopCities.join(', ')})</span>
              )}
            </div>
          </div>
          
          {/* Arrival */}
          <div style={styles.timeBlock}>
            {arrivalTime ? (
              <div style={styles.time}>
                {arrivalTime}
                {nextDay && <span style={styles.nextDay}>+1</span>}
              </div>
            ) : (
              <div style={styles.noTime}>--:--</div>
            )}
            <div style={styles.airport}>{destination}</div>
          </div>
        </div>
        
        {/* Price */}
        <div style={styles.priceSection}>
          <div style={styles.priceLabel}>from</div>
          <div style={styles.priceAmount}>${Math.round(price)}</div>
          <div style={styles.pricePer}>per person</div>
          {passengers > 1 && (
            <div style={styles.priceTotal}>${Math.round(totalPrice)} total</div>
          )}
        </div>
      </div>
      
      {/* Booking Options */}
      <div style={styles.bookingSection}>
        <div style={styles.bookingHeader}>
          <span style={styles.bookingTitle}>Book with:</span>
          {bookingOptions.length > 4 && (
            <button 
              style={styles.showMoreBtn}
              onClick={() => setShowAllOptions(!showAllOptions)}
            >
              {showAllOptions ? 'Show less' : `+${bookingOptions.length - 4} more`}
            </button>
          )}
        </div>
        
        <div style={styles.bookingRows}>
          {displayedOptions.map((option, idx) => (
            <div 
              key={option.id}
              style={option.isBest ? styles.bookingRowBest : styles.bookingRow}
              onClick={(e) => handleBookClick(option, e)}
              role="button"
              tabIndex={0}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateX(4px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateX(0)'; }}
            >
              <div style={styles.rowLogo}>
                {option.isImage && option.logo ? (
                  <img 
                    src={option.logo} 
                    alt={option.name}
                    style={styles.rowLogoImg}
                    onError={(e) => { 
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <span>🔗</span>
                )}
              </div>
              <span style={styles.rowName}>{option.name}</span>
              {option.badge && (
                <span style={styles.rowBadge}>{option.badge}</span>
              )}
              {option.isDeepLink && (
                <span style={styles.rowBadgeAffiliate}>Affiliate</span>
              )}
              <span style={option.isBest ? styles.rowPriceBest : styles.rowPrice}>
                {option.priceLabel}
              </span>
              <span style={styles.rowArrow}>→</span>
            </div>
          ))}
        </div>
        
        {/* Data source indicator */}
        <div style={styles.dataSource}>
          <span style={styles.sourceDot}></span>
          <span>
            Prices from {source === 'travelpayouts' ? 'Aviasales' : 
                         source === 'amadeus' ? 'Amadeus' : source}
          </span>
        </div>
      </div>
    </div>
  );
};

export default OneWayFlightCard;
